import { useImperativeHandle, useRef, useState } from 'react'

import { useBoolean } from 'ahooks'
import { Divider } from 'antd'

import { authenticateIdentityApi } from '@eastcoal/api/trade/auctionMall/authenticateIdentity'
import { checkIdentityAuthenticationApi } from '@eastcoal/api/trade/auctionMall/checkIdentityAuthentication'

import { DraggableModal } from '@eastcoal/common/components/DraggableModal'
import { SmsInput } from '@eastcoal/common/components/SmsInput'
import { Noop } from '@eastcoal/common/types/utilities'
import { pick } from '@eastcoal/common/utils/object'

import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { SchemaForm } from '@eastcoal/formily/components/SchemaForm'
import { SchemaItem } from '@eastcoal/formily/components/SchemaForm/interface'
import { useCreationForm } from '@eastcoal/formily/hooks/useCreationForm'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'

import { SmsTemplateCode } from '../../../enums/SmsTemplateCode'
import { useAuctionDetailAuthenticatorStore } from '../Store/hooks/useStore'

import { AuctionDetailAuthenticatorFormScope } from './interface'

const createInitialFormScore = () => ({
  bizId: '',
  bizType: '',
  auctionTitle: ''
})

const items: SchemaItem[] = [
  {
    name: 'agentName',
    type: 'string',
    title: '业务受托人',
    'x-pattern': 'readPretty',
    'x-decorator': FormItem,
    'x-component': Input
  },
  {
    name: 'phone',
    type: 'string',
    title: '手机号',
    'x-pattern': 'readPretty',
    'x-decorator': FormItem,
    'x-component': Input
  },
  {
    name: '{code,ticketId}',
    type: 'object',
    title: '短信验证码',
    required: true,
    'x-component': SmsInput,
    'x-component-props': {
      templateCode: SmsTemplateCode.TRADE_SESSION_BID,
      phone: '{{ $values.phone }}',
      bizId: '{{ bizId }}',
      bizType: '{{ bizType }}',
      templateParam: {
        auction_title: '{{ auctionTitle }}'
      },
      comment: '前往出价身份验证'
    },
    'x-decorator': FormItem
  }
]
export const AuctionDetailAuthenticatorCore = () => {
  const { member } = useAuthStore(state => pick(state, ['member']))
  const form = useCreationForm({
    initialValues: {
      agentName: member.agentName,
      phone: member.agentPhone
    }
  })
  const [formScope, setFormScope] =
    useState<AuctionDetailAuthenticatorFormScope>(() =>
      createInitialFormScore()
    )
  const [modelVisible, modalVisibleActions] = useBoolean(false)
  const { ref } = useAuctionDetailAuthenticatorStore(state =>
    pick(state, ['ref'])
  )
  const promiseResolveRef = useRef<Noop>(null)
  const promiseRejectRef = useRef<Noop>(null)

  useImperativeHandle(ref, () => ({
    doCheck: async options => {
      setFormScope(options)
      const isAuthenticated = await checkIdentityAuthenticationApi({
        auctionId: options.bizId
      })
      if (!isAuthenticated) {
        modalVisibleActions.setTrue()
        const promise = new Promise<void>((resolve, reject) => {
          promiseResolveRef.current = resolve
          promiseRejectRef.current = reject
        })
        return promise
      }
    }
  }))

  // 关闭弹窗并重置表单
  const closeModal = () => {
    modalVisibleActions.setFalse()
    form.reset()
    setFormScope(createInitialFormScore())
  }

  const handleModalCancel = () => {
    closeModal()
    promiseRejectRef.current?.()
  }

  const [submitting, submittingActions] = useBoolean(false)
  const handleModalOk = async () => {
    try {
      submittingActions.setTrue()
      await form.submit(values =>
        authenticateIdentityApi({
          ...values,
          auctionId: formScope.bizId
        })
      )
      closeModal()
      promiseResolveRef.current?.()
    } finally {
      submittingActions.setFalse()
    }
  }

  return (
    <DraggableModal
      open={modelVisible}
      title='竞价入场验证'
      mask={{
        closable: false
      }}
      okButtonProps={{
        loading: submitting
      }}
      onCancel={handleModalCancel}
      onOk={handleModalOk}
    >
      <div>
        为提升出价和交易的安全性，请提供业务受托人的短信验证码，进行出价身份验证。
      </div>
      <Divider />
      <SchemaForm
        form={form}
        name='auctionDetailAuthenticatorForm'
        schema={items}
        scope={formScope}
        layout={{
          wrapperWidth: 300
        }}
      />
    </DraggableModal>
  )
}
